import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, BadgePercent, CalendarDays, MapPin, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { offersAPI } from '../services/api';
import { resolveApiErrorMessage } from '../utils/apiErrors';
import { extractOfferDetail, extractPayloadValue, formatOfferDate, getOfferCategoryKey, normalizeOffer } from '../utils/offers';
import { useLocalization } from '../context/LocalizationContext';
import { InterestStatusBadge } from '../components/offers/InterestStatusBadge';

export const OfferDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { offerId } = useParams();
  const { needsCompactFont, locale, t } = useLocalization();
  const routeOffer = useMemo(
    () => (location.state?.offer ? normalizeOffer(location.state.offer) : null),
    [location.state?.offer]
  );
  const selectedCity = useMemo(() => String(location.state?.selectedCity || '').trim(), [location.state]);

  const [offer, setOffer] = useState(routeOffer);
  const [loading, setLoading] = useState(!routeOffer);
  const [submittingInterest, setSubmittingInterest] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    const fetchOfferDetails = async () => {
      if (!offerId) {
        setErrorMessage(t('offers.offerUnavailable'));
        return;
      }

      setLoading((previous) => previous || !routeOffer);
      setErrorMessage('');
      try {
        const response = await offersAPI.getOfferDetails(offerId);
        const detail = extractOfferDetail(response.data);
        if (!detail) {
          throw new Error(t('offers.offerUnavailable'));
        }
        if (!cancelled) {
          setOffer((previous) => ({ ...(previous || {}), ...detail }));
        }
      } catch (error) {
        if (cancelled) return;
        const message = resolveApiErrorMessage(error, t, t('offers.failedToLoadDetail'));
        setErrorMessage(message);
        toast.error(message);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchOfferDetails();

    return () => {
      cancelled = true;
    };
  }, [language, offerId, routeOffer, t]);

  const handleSubmitInterest = async () => {
    if (!offerId) return;
    setSubmittingInterest(true);

    try {
      const payload = {
        userConsentToShareBasicDetails: true,
      };
      if (selectedCity) {
        payload.city = selectedCity;
      }

      const response = await offersAPI.submitInterest(offerId, payload);
      const interestPayload = extractPayloadValue(response.data) || {};

      setOffer((previous) => ({
        ...(previous || {}),
        interestAlreadySubmitted: true,
        interestStatus: String(interestPayload.interestStatus || previous?.interestStatus || 'SUBMITTED').toUpperCase(),
        referenceId: interestPayload.referenceId || previous?.referenceId || '',
      }));

      toast.success(t('offers.interestSubmitted'));
    } catch (error) {
      const message = resolveApiErrorMessage(error, t, t('offers.interestFailed'));
      toast.error(message);
    } finally {
      setSubmittingInterest(false);
    }
  };

  const effectiveOffer = offer || routeOffer;

  return (
    <DashboardLayout>
      <div className="app-page-shell fade-in max-w-5xl" data-testid="offer-details-page">
        <div className="pointer-events-none absolute -z-10 inset-0 dark:hidden">
          <div className="absolute -top-10 left-10 h-48 w-48 rounded-full bg-primary-200/35 blur-3xl" />
          <div className="absolute top-20 right-6 h-56 w-56 rounded-full bg-primary-200/25 blur-3xl" />
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => navigate('/offers')}
              className="inline-flex items-center gap-2 text-sm font-medium text-primary-700 transition-colors hover:text-primary-600 dark:text-slate-300 dark:hover:text-primary-300"
            >
              <ArrowLeft size={16} />
              {t('offers.backToOffers')}
            </button>
            <h1 className={`app-page-heading ${needsCompactFont ? 'app-page-heading-te' : ''}`}>
              {t('offers.offerDetailsTitle')}
            </h1>
            <p className="app-page-subtitle">{t('offers.offerDetailsSubtitle')}</p>
          </div>
        </div>

        {loading ? (
          <div className="app-surface p-10">
            <div className="flex items-center justify-center gap-3 text-primary-800 dark:text-primary-200">
              <RefreshCw size={20} className="animate-spin text-primary-500" />
              <span>{t('offers.loadingDetail')}</span>
            </div>
          </div>
        ) : !effectiveOffer ? (
          <div className="app-surface p-10 text-center">
            <h2 className="font-serif text-2xl font-semibold text-primary-900 dark:text-white">
              {t('offers.offerUnavailable')}
            </h2>
            {errorMessage && (
              <p className="mt-2 text-sm text-primary-700 dark:text-primary-300">{errorMessage}</p>
            )}
          </div>
        ) : (
          <div className="space-y-5">
            {!!errorMessage && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-200">
                {errorMessage}
              </div>
            )}

            <section className="app-hero-surface overflow-hidden p-5 sm:p-6">
              {effectiveOffer.bannerImageUrl && (
                <img
                  src={effectiveOffer.bannerImageUrl}
                  alt={effectiveOffer.title}
                  className="mb-5 h-48 w-full rounded-3xl object-cover sm:h-64"
                  onError={(event) => {
                    event.currentTarget.style.display = 'none';
                  }}
                />
              )}

              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white/90">
                      {effectiveOffer.brandName}
                    </span>
                    <span className="rounded-full bg-white/20 px-3 py-1 text-xs font-semibold text-white/90">
                      {t(`offers.categories.${getOfferCategoryKey(effectiveOffer.category)}`)}
                    </span>
                    {effectiveOffer.featured && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-white/20 px-3 py-1 text-xs font-semibold text-white/90">
                        <BadgePercent size={12} />
                        {t('offers.featuredBadge')}
                      </span>
                    )}
                  </div>

                  <div>
                    <h2 className="font-serif text-3xl font-bold tracking-tight sm:text-4xl">{effectiveOffer.title}</h2>
                    {effectiveOffer.shortSummary && (
                      <p className="mt-3 max-w-3xl text-sm leading-7 text-primary-50 sm:text-base">
                        {effectiveOffer.shortSummary}
                      </p>
                    )}
                  </div>

                  <div className="grid gap-3 text-sm sm:grid-cols-2">
                    <div className="flex items-center gap-2 text-primary-50">
                      <MapPin size={16} />
                      <span>{effectiveOffer.cityLabel || t('offers.availableAcrossIndia')}</span>
                    </div>
                    <div className="flex items-center gap-2 text-primary-50">
                      <CalendarDays size={16} />
                      <span>
                        {effectiveOffer.validTill
                          ? t('offers.validTillValue', { date: formatOfferDate(effectiveOffer.validTill, locale) })
                          : t('offers.validWhileActive')}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="w-full max-w-sm rounded-3xl border border-white/20 bg-white/10 p-4 backdrop-blur-md">
                  {effectiveOffer.interestAlreadySubmitted ? (
                    <div className="space-y-4">
                      <InterestStatusBadge status={effectiveOffer.interestStatus} t={t} />
                      <div className="space-y-2 text-sm text-primary-50">
                        <p>{t('offers.interestAlreadySubmitted')}</p>
                        {effectiveOffer.referenceId && (
                          <p>
                            <span className="font-semibold">{t('offers.referenceId')}:</span> {effectiveOffer.referenceId}
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      <p className="text-sm leading-6 text-primary-50">{t('offers.consentNote')}</p>
                      <button
                        type="button"
                        onClick={handleSubmitInterest}
                        disabled={submittingInterest}
                        className="inline-flex w-full items-center justify-center rounded-2xl bg-white px-4 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        {submittingInterest ? t('offers.submittingInterest') : t('offers.imInterested')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </section>

            <section className="grid gap-5 lg:grid-cols-[1.35fr_0.95fr]">
              <div className="space-y-5">
                <div className="app-surface p-5">
                  <h3 className="app-panel-title">{t('offers.detailsSectionTitle')}</h3>
                  <p className="mt-3 text-sm leading-7 text-primary-800 dark:text-primary-200">
                    {effectiveOffer.description || effectiveOffer.shortSummary || t('offers.descriptionUnavailable')}
                  </p>
                </div>

                <div className="app-surface p-5">
                  <h3 className="app-panel-title">{t('offers.termsTitle')}</h3>
                  <p className="mt-3 text-sm leading-7 text-primary-800 dark:text-primary-200">
                    {effectiveOffer.termsAndConditions || t('offers.termsUnavailable')}
                  </p>
                </div>
              </div>

              <div className="space-y-5">
                <div className="app-highlight-surface p-5">
                  <h3 className="app-panel-title">{t('offers.disclaimerTitle')}</h3>
                  <p className="mt-3 text-sm leading-7 text-primary-800 dark:text-primary-200">
                    {effectiveOffer.disclaimer || t('offers.partnerDisclaimer')}
                  </p>
                </div>

                <div className="app-surface p-5">
                  <h3 className="app-panel-title">{t('offers.contactModeTitle')}</h3>
                  <p className="mt-3 text-sm leading-7 text-primary-800 dark:text-primary-200">
                    {effectiveOffer.partnerContactMode || t('offers.contactModeFallback')}
                  </p>
                  {selectedCity && (
                    <p className="mt-4 text-sm text-primary-700 dark:text-primary-300">
                      {t('offers.contextUsingCity', { city: selectedCity })}
                    </p>
                  )}
                </div>
              </div>
            </section>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default OfferDetails;
